import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CoinGecko coin IDs mapping (for reference/display)
const COINGECKO_IDS = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  USDT: 'tether',
  BNB: 'binancecoin',
  SOL: 'solana',
  XRP: 'ripple',
  ADA: 'cardano',
  DOGE: 'dogecoin',
};

const COIN_NAMES = {
  BTC: 'Bitcoin',
  ETH: 'Ethereum',
  USDT: 'Tether',
  BNB: 'BNB',
  SOL: 'Solana',
  XRP: 'XRP',
  ADA: 'Cardano',
  DOGE: 'Dogecoin',
};

// Minimum interval between API calls (to respect rate limits)
const MIN_API_INTERVAL = 15000;

const MarketDataContext = createContext(null);

export function MarketDataProvider({ children }) {
  const [priceData, setPriceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const lastFetchTime = useRef(0);
  const fetchInProgress = useRef(false);
  const intervalRef = useRef(null);

  const fetchPriceData = useCallback(async (force = false) => {
    const now = Date.now();
    
    // Prevent concurrent fetches and respect rate limits
    if (fetchInProgress.current) return;
    if (!force && now - lastFetchTime.current < MIN_API_INTERVAL) return;
    
    fetchInProgress.current = true;
    
    try {
      const response = await fetch(`${API_URL}/api/market/prices`);
      
      if (response.status === 429) {
        console.warn('Rate limited - using cached data');
        setError('Rate limited - showing cached data');
        return;
      }
      
      if (!response.ok) throw new Error('API request failed');
      
      const result = await response.json();
      
      setPriceData(result.data || {});
      setLastUpdated(new Date(result.timestamp));
      setError(null);
      lastFetchTime.current = now;
    } catch (err) {
      console.error('Failed to fetch market data:', err);
      // Only set error if we have no cached data
      if (Object.keys(priceData).length === 0) {
        setError('Unable to fetch live data');
      }
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [priceData]);

  const fetchChartData = useCallback(async (symbol, days) => {
    try {
      const response = await fetch(`${API_URL}/api/market/chart/${symbol}?days=${days}`);
      
      if (response.status === 429) {
        console.warn('Rate limited for chart data');
        return null;
      }
      
      if (!response.ok) throw new Error('Failed to fetch chart data');
      
      const result = await response.json();
      
      return {
        chartData: result.chartData.map(item => ({
          time: new Date(item.time).toLocaleDateString(),
          price: item.price,
        })),
        currentPrice: result.currentPrice,
        priceChange: result.priceChange,
      };
    } catch (err) {
      console.error('Failed to fetch chart:', err);
      return null;
    }
  }, []);

  // Initial fetch and setup interval
  useEffect(() => {
    fetchPriceData(true);
    
    // Refresh every 30 seconds
    intervalRef.current = setInterval(() => fetchPriceData(), 30000);
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchPriceData]);

  const value = {
    priceData,
    loading,
    error,
    lastUpdated,
    fetchPriceData,
    fetchChartData,
    COINGECKO_IDS,
    COIN_NAMES,
  };

  return (
    <MarketDataContext.Provider value={value}>
      {children}
    </MarketDataContext.Provider>
  );
}

export function useMarketData() {
  const context = useContext(MarketDataContext);
  if (!context) {
    throw new Error('useMarketData must be used within a MarketDataProvider');
  }
  return context;
}

// Export constants for use in components
export { COINGECKO_IDS, COIN_NAMES };
